import { makeAutoObservable, reaction } from 'mobx';

import { disciplineCategoryAliases, pureOutrightsDisciplines } from '@/config/constants';

import { RootStoreMobx } from '../RootStoreMobx';

export type UDisciplinesCategoriesNames = 'sport' | 'eSport' | 'vSport' | 'racing';

interface IDisciplineCount {
  id: string;
  category: number;
  live: number;
  name: string;
  prematch: number;
}

export interface IDisciplineCategory extends IDisciplineCount {
  isOutright: boolean;
}

type UCategorizedDisciplines = {
  [K in UDisciplinesCategoriesNames]?: IDisciplineCategory[];
};

class Categorizer {
  root: RootStoreMobx;
  private disposer: () => void;

  constructor(model: RootStoreMobx) {
    makeAutoObservable(this);

    this.root = model;

    // Update the categorizer with the new lobby disciplines list
    this.disposer = reaction(
      () => this.root.store.lobbyDisciplinesList,
      (lobbyDisciplinesList) => {
        this.root.categorizer.categoryResolver(lobbyDisciplinesList);
      },
    );
  }

  dispose = (): void => {
    if (this.disposer) {
      this.disposer();
    }
  };

  NUMBER_PRIME_DISCIPLINES: number = 6 as const;

  /**
   * Determines which categorizer to show 'basic' or 'prime'
   */
  sportsNavigation: 'basic' | 'prime' = 'basic';

  /**
   * Passes the line filter state to a categorizer
   * Used to filter disciplines by line ('sport', 'esport')
   */
  categorizerFilterLine: string = '';

  /**
   * Flag indicating whether the 'prime' categorizer is active
   */
  isPrimeCategorizer: boolean = false;

  /**
   * Stores categorized disciplines organized by type
   * Each type (sport, eSport, vSport, racing) contains an array of disciplines if category available
   */
  categorizedDisciplines: UCategorizedDisciplines = {};

  /**
   * Stores prime picks disciplines
   * This is a subset of disciplines that are have high 'topIndex'
   */
  primePicks: IDisciplineCategory[] = [];

  /**
   * Sets the categorizer type ('basic' or 'prime').
   */
  setSportsNavigation = (type: 'basic' | 'prime'): void => {
    this.sportsNavigation = type;
    this.isPrimeCategorizer = type === 'prime';
  };

  /**
   * Sets the line filter state for the categorizer
   */
  setCategorizerFilterLine = (line: string = ''): void => {
    this.categorizerFilterLine = line;
  };

  /**
   * Organizes disciplines into categories based on their type
   * The categories are defined in the `disciplineCategoryAliases` configuration.
   */
  categoryResolver = (disciplines: IDisciplineCount[]): void => {
    const prime = [];

    this.categorizedDisciplines = disciplines.reduce((acc, discipline, i) => {
      const { category, id } = discipline;
      const alias = disciplineCategoryAliases[category];
      discipline.isOutright = pureOutrightsDisciplines.has(id);

      if (i < this.NUMBER_PRIME_DISCIPLINES) {
        prime.push(discipline);
      }

      if (!acc[alias]) {
        acc[alias] = [];
      }

      acc[alias].push(discipline);
      return acc;
    }, {} as UCategorizedDisciplines);

    this.primePicks = prime;
  };
}

export { Categorizer };
